import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useScope } from "@/context/ScopeContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import EnrolmentExecutiveTab from "@/components/EnrolmentExecutiveTab";
import EnrolmentClassStageTab from "@/components/EnrolmentClassStageTab";
import EnrolmentGenderTab from "@/components/EnrolmentGenderTab";
import EnrolmentRetentionRiskTab from "@/components/EnrolmentRetentionRiskTab";
import EnrolmentInsightsTab from "@/components/EnrolmentInsightsTab";
import DashboardPageHeader from "@/components/DashboardPageHeader";
import "@/components/EnrolmentExecutiveTab.css";
import "@/components/EnrolmentClassStageTab.css";
import "@/components/EnrolmentGenderTab.css";
import "@/components/EnrolmentRetentionRiskTab.css";
import "@/components/EnrolmentInsightsTab.css";
import { 
  Users, 
  GraduationCap,
  RefreshCw,
  Upload,
  TrendingUp,
  TrendingDown,
  UserCheck,
  BarChart3,
  Target,
  Activity,
  Brain
} from "lucide-react";
import { toast } from "sonner";
import ExportPanel from "@/components/ExportPanel";
import { getBackendUrl } from "@/lib/backend";

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const TAB_ITEMS = [
  { id: "executive", label: "Executive Overview", icon: BarChart3 },
  { id: "classwise", label: "Class & Stage", icon: GraduationCap },
  { id: "gender", label: "Gender & Inclusion", icon: Users },
  { id: "retention", label: "Retention & Risk", icon: Activity },
  { id: "insights", label: "Insights", icon: Brain },
];

// KPI Card Component
const KPICard = ({ label, value, suffix = "", icon: Icon, color = "blue", description, trend, trendValue }) => {
  const colors = {
    blue: "bg-blue-50 text-blue-600",
    green: "bg-emerald-50 text-emerald-600",
    red: "bg-red-50 text-red-600",
    amber: "bg-amber-50 text-amber-600",
    purple: "bg-purple-50 text-purple-600",
    cyan: "bg-cyan-50 text-cyan-600",
  };

  return (
    <Card className="border-slate-200" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">{label}</p>
            <div className="flex items-baseline gap-1 mt-1">
              <span className="text-2xl font-bold text-slate-900 tabular-nums">
                {typeof value === 'number' ? value.toLocaleString('en-IN') : value}
              </span>
              {suffix && <span className="text-lg text-slate-500">{suffix}</span>}
            </div>
            {trend !== undefined && trendValue !== undefined && (
              <div className={`flex items-center gap-1 mt-1 text-sm ${trend === 'up' ? 'text-emerald-600' : trend === 'down' ? 'text-red-600' : 'text-slate-500'}`}>
                {trend === 'up' ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                <span>{trendValue}</span>
              </div>
            )}
            {description && <p className="text-xs text-slate-400 mt-1">{description}</p>}
          </div>
          {Icon && (
            <div className={`p-2 rounded-lg ${colors[color]}`}>
              <Icon className="w-5 h-5" strokeWidth={1.5} />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const EnrolmentDashboard = () => {
  const { scope } = useScope();
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [overview, setOverview] = useState(null);
  const [classData, setClassData] = useState([]);
  const [stageData, setStageData] = useState([]);
  const [sizeDistribution, setSizeDistribution] = useState([]);
  const [blockData, setBlockData] = useState([]);
  const [retentionData, setRetentionData] = useState([]);
  const [smallSchools, setSmallSchools] = useState([]);
  const [largeSchools, setLargeSchools] = useState([]);
  const [managementWiseData, setManagementWiseData] = useState([]);
  const [activeTab, setActiveTab] = useState("executive");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [
        overviewRes,
        classRes,
        stageRes,
        sizeRes,
        blockRes,
        retentionRes,
        smallRes,
        largeRes,
        managementRes
      ] = await Promise.all([
        axios.get(`${API}/enrolment/overview`),
        axios.get(`${API}/enrolment/class-wise`),
        axios.get(`${API}/enrolment/stage-wise`),
        axios.get(`${API}/enrolment/school-size-distribution`),
        axios.get(`${API}/enrolment/block-wise`),
        axios.get(`${API}/enrolment/retention-analysis`),
        axios.get(`${API}/enrolment/risk-schools?risk_type=small`),
        axios.get(`${API}/enrolment/risk-schools?risk_type=large`),
        axios.get(`${API}/enrolment/management-wise`)
      ]);
      
      setOverview(overviewRes.data);
      setClassData(classRes.data);
      setStageData(stageRes.data);
      setSizeDistribution(sizeRes.data);
      setBlockData(blockRes.data);
      setRetentionData(retentionRes.data);
      setSmallSchools(smallRes.data);
      setLargeSchools(largeRes.data);
      setManagementWiseData(Array.isArray(managementRes.data) ? managementRes.data : []);
    } catch (error) {
      console.error("Error fetching Enrolment data:", error);
      toast.error("Failed to load enrolment data");
    } finally {
      setLoading(false);
    }
  }, [
    scope.version,
    scope.districtCode,
    scope.blockCode,
    scope.udiseCode,
    scope.districtName,
    scope.blockName,
    scope.schoolName,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleImport = async () => {
    const url = "https://customer-assets.emergentagent.com/job_7d6cbc1e-b567-4fbc-af84-06fa5107bbd4/artifacts/l66i3550_4.%20Enrolment_Class_Wise_All_Student%20%20-%202025-26%20-%20%28State%20%29%20MAHARASHTRA.xlsx";
    
    setImporting(true);
    try {
      await axios.post(`${API}/enrolment/import?url=${encodeURIComponent(url)}`);
      toast.success("Enrolment data import started!");
      setTimeout(() => {
        fetchData();
        setImporting(false);
      }, 5000);
    } catch (error) {
      toast.error("Import failed: " + (error.response?.data?.detail || error.message));
      setImporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="loading-spinner" />
      </div>
    );
  }

  const hasData = overview && overview.total_schools > 0;

  const scopeLabel =
    scope.schoolName?.trim() ||
    scope.blockName?.trim() ||
    scope.districtName?.trim() ||
    "Pune District";

  const headerCopy = {
    executive: {
      subtitle:
        "A cleaner executive overview that explains enrolment scale, gender balance, stage mix, school-size load and data quality in one decision-ready page.",
    },
    classwise: {
      subtitle:
        "Class and stage distribution redesigned to help officers quickly identify concentration, transition pressure, gender balance and classes requiring focused retention action.",
    },
    gender: {
      eyebrow: "Gender & inclusion",
      subtitle:
        "Gender parity, representation and inclusion signals redesigned for faster executive scanning, clear risk identification and class-wise action planning.",
    },
    retention: {
      eyebrow: "Retention & transition risk",
      subtitle:
        "Retention transitions, dropout risk hotspots, school viability watchlists and block-level intervention priorities for district review.",
    },
    insights: {
      eyebrow: "Insights",
      subtitle:
        "AI-powered enrolment insights, root-cause signals, priority actions and district review register.",
    },
  };

  const copy = headerCopy[activeTab] || headerCopy.executive;
  const eyebrowIcon =
    activeTab === "gender"
      ? Users
      : activeTab === "classwise"
        ? GraduationCap
        : activeTab === "retention"
          ? Activity
          : activeTab === "insights"
            ? Brain
            : BarChart3;
  const EyebrowIcon = eyebrowIcon;
  const eyebrowLabel =
    copy.eyebrow || "Class-wise Student Enrolment";

  return (
    <div className="enr-dashboard space-y-6 animate-fade-in" data-testid="enrolment-dashboard">
      <DashboardPageHeader
        title="Enrolment Analytics Dashboard"
        subtitle={
          hasData
            ? copy.subtitle
            : "Class-wise Student Enrolment • AY 2025-26 • Pune District"
        }
        eyebrow={
          hasData ? (
            <>
              <EyebrowIcon size={14} />
              {eyebrowLabel} · AY 2025–26 · {scopeLabel}
            </>
          ) : null
        }
        actions={
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={handleImport}
              disabled={importing}
              data-testid="import-enrolment-btn"
            >
              {importing ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              {importing ? "Importing..." : "Import Data"}
            </Button>
            <ExportPanel dashboardName="enrolment" dashboardTitle="Enrolment Analytics" />
            <Button variant="outline" size="sm" onClick={fetchData} data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </>
        }
      />

      {!hasData ? (
        <Card className="border-slate-200">
          <CardContent className="py-12 text-center">
            <GraduationCap className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No Enrolment Data Available</h3>
            <p className="text-slate-500 mb-4">Click "Import Data" to load the Class-wise Enrolment Excel file</p>
            <Button onClick={handleImport} disabled={importing} data-testid="import-enrolment-empty-btn">
              {importing ? "Importing..." : "Import Enrolment Data"}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Dashboard Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <div className="enr-tabs-wrap" aria-label="Enrolment dashboard sections">
              <nav className="enr-tabs">
                {TAB_ITEMS.map(({ id, label, icon: Icon }) => (
                  <button
                    key={id}
                    type="button"
                    className={`enr-tab ${activeTab === id ? "active" : ""}`}
                    onClick={() => setActiveTab(id)}
                    data-testid={`tab-${id}`}
                  >
                    <Icon />
                    {label}
                  </button>
                ))}
              </nav>
            </div>

            <TabsContent value="executive" className="mt-0">
              <EnrolmentExecutiveTab
                overview={overview}
                stageData={stageData}
                sizeDistribution={sizeDistribution}
                classData={classData}
                managementWiseData={managementWiseData}
                blockData={blockData}
              />
            </TabsContent>

            <TabsContent value="classwise" className="mt-0">
              <EnrolmentClassStageTab overview={overview} classData={classData} />
            </TabsContent>

            <TabsContent value="gender" className="mt-0">
              <EnrolmentGenderTab overview={overview} classData={classData} stageData={stageData} />
            </TabsContent>

            <TabsContent value="retention" className="mt-0">
              <EnrolmentRetentionRiskTab
                overview={overview}
                retentionData={retentionData}
                blockData={blockData}
                smallSchools={smallSchools}
                largeSchools={largeSchools}
                sizeDistribution={sizeDistribution}
                classData={classData}
                onViewInsights={() => setActiveTab("insights")}
              />
            </TabsContent>
            <TabsContent value="insights" className="mt-0">
              <EnrolmentInsightsTab
                overview={overview}
                blockData={blockData}
                sizeDistribution={sizeDistribution}
              />
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
};

export default EnrolmentDashboard;
